import React, { useEffect, useMemo, useState } from "react";
import { Input } from "@etiquette-ui/inputs-dropdowns";
import { Subtitle, Label, P } from "@etiquette-ui/typography";
import useI18n from "i18n/useI18n";
import ITEM_KEYS from "../../keys";
import { ACTIONS } from "../../Reducer";
import usePOS from "state/pos";
import { POS_ENABLED } from "resources/constants/config";
import { DotPulse } from "@uiball/loaders";
import POSCodeInput from "./components/POSCodeInput";
import PricingMethodSelect from "./components/PricingMethodSelect";
import Switches from "../../../../../../components/Switches";
import SecondaryButton from "../../../../../../components/SecondaryButton";
import TimeBasedStrategy from "./components/TimeBasedStrategy";

import { PRICING_STRATEGIES } from "pages/MenuItems/pages/Items/resources/constants";

import {
    Row,
    PresentationSection,
    PresentationBody,
    TitleContainer,
    InputsContainer,
    NameContainer,
    CustomFormSection,
} from "./Styles";


const Presentations = ({ formData, dispatch }) => {
    const { translate } = useI18n();
    const [pos, posDispatcher, posUtils] = usePOS();
    const [posSync, setPosSync] = useState(null);
    const [posChange, setPosChange] = useState(null);

    useEffect(() => {
        if (POS_ENABLED) {
            posDispatcher.fetchStart();
        }
    }, [posDispatcher]);

    useEffect(() => {
        if (pos.list.length === 0 || !formData[ITEM_KEYS.VARIANTS] || formData[ITEM_KEYS.VARIANTS].length === 0) setPosSync(null);
        
        const posSync = {};
        const posList = !!formData[ITEM_KEYS.VARIANTS]
                        ? posUtils.findAll(formData[ITEM_KEYS.VARIANTS].map(({ id }) => id))
                        : [];

        for (const pos of posList) {
            const { posCode, posId, price } = pos;
            posSync[pos.variantId] = { posCode, posId, price };
        }

        setPosSync(posSync);
        setPosChange(null);
    }, [pos.list, posUtils, formData]);

    const showAdditionalInfo = useMemo(() => 
        !!formData[ITEM_KEYS.VARIANTS] && formData[ITEM_KEYS.VARIANTS].length > 1,
    [formData]);
    
    return !!formData[ITEM_KEYS.VARIANTS] && (
        <PresentationBody>
            {formData[ITEM_KEYS.VARIANTS].map((variant, i) => (
                <CustomFormSection>
                    <TitleContainer>
                        <div style={{ display: "flex", gap: 10, alignItems: "center"}}>
                            <Subtitle>{translate("presentation")} {i+1} {pos.fetching.state && <DotPulse size={22} color={"black"} />}</Subtitle>
                        </div>
                        {!showAdditionalInfo && (
                            <Label>({translate("presentations_clarification_text")})</Label>
                        )}
                    </TitleContainer>                    
                    <Row style={{alignItems: 'center'}}>
                        <InputsContainer>
                            <PricingMethodSelect
                                pricingStrategy={variant.pricingStrategy}
                                setPricingStrategy={(value) => 
                                    dispatch(ACTIONS.VARIANT, {
                                        idx: i,
                                        variant: {
                                            ...variant,
                                            pricingStrategy: value,
                                        },
                                    })
                                }
                            />
                            {showAdditionalInfo && (
                                <NameContainer>
                                    <Input
                                        className={'double'}
                                        labelProps={{ label: translate("presentation_name_en") }}
                                        iconProps={{ icon: "pos" }}
                                        inputProps={{
                                            value: variant.translations?.en,
                                            onChange: (e) =>
                                                dispatch(ACTIONS.VARIANT, {
                                                    idx: i,
                                                    variant: {
                                                        ...variant,
                                                        translations: {
                                                            ...variant.translations,
                                                            en: e.target.value,
                                                        },
                                                    },
                                                }),
                                            required: true,
                                        }}
                                        parentContainerClassName='presentation-name'
                                    />
                                    <Input
                                        className={'double'}
                                        labelProps={{ label: translate("presentation_name_es") }}
                                        iconProps={{ icon: "pos" }}
                                        inputProps={{
                                            value: variant.translations?.es,
                                            onChange: (e) =>
                                                dispatch(ACTIONS.VARIANT, {
                                                    idx: i,
                                                    variant: {
                                                        ...variant,
                                                        translations: {
                                                            ...variant.translations,
                                                            es: e.target.value,
                                                        },
                                                    },
                                                }),
                                            required: true,
                                        }}
                                        parentContainerClassName='presentation-name'
                                    />
                                </NameContainer>
                            )}
                            {variant.pricingStrategy !== PRICING_STRATEGIES.mktPrice && (
                                <Input
                                    labelProps={{ label: translate("Price") }}
                                    iconProps={{ icon: "price" }}
                                    inputProps={{
                                        value: variant.price,
                                        onChange: (e) =>
                                            dispatch(ACTIONS.VARIANT, {
                                                idx: i,
                                                variant: {
                                                    ...variant,
                                                    price: +e.target.value,
                                                },
                                            }),
                                        type: "number",
                                        required: true,
                                    }}
                                    parentContainerClassName="price"
                                />
                            )}
                            
                            <POSCodeInput 
                                variantId={variant.id} 
                                posSync={posSync ? posSync[variant.id] :  null}
                                inputClassName='pos-code'
                            />                    
                        </InputsContainer>

                        {showAdditionalInfo && (
                            <Switches
                                active={formData[ITEM_KEYS.VARIANTS][i].active}
                                onDisable={() =>
                                    dispatch(ACTIONS.VARIANT, {
                                        idx: i,
                                        variant: {
                                            ...variant,
                                            active: !variant.active,
                                        },
                                    })
                                }
                                onDelete={() => dispatch(ACTIONS.REMOVE_VARIANT, i)}
                            />
                        )}
                    </Row>
                    {
                        {
                            [PRICING_STRATEGIES.timeBasedPrice]: <TimeBasedStrategy 
                                pricingStrategy={variant.pricingStrategy} 
                                variant={variant} 
                                onChange={(variant)=>dispatch(ACTIONS.VARIANT, {idx: i, variant: variant})}
                                />
                        }[variant.pricingStrategy]                        
                    }                
                </CustomFormSection>
            ))}
            <PresentationSection>
                <SecondaryButton
                    onClick={() => dispatch(ACTIONS.NEW_VARIANT, null)}
                    label={translate("add_presentation")}
                />
            </PresentationSection>
        </PresentationBody>
    );
};

export default Presentations;
